// dashboardPersediaan/PerbandinganPage.js
import React, { useState, useEffect } from "react";
import { Button, Spinner, Card, CardBody, Row, Col } from "reactstrap";
import * as Icons from "react-feather";
import Axios from "axios";
import moment from "moment";
import { urlAPI, getToken } from "utility/Common";

const PerbandinganPage = ({ onBack, currentBulan, currentTahun, previousBulan, previousTahun }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1);
  const [selectedYear, setSelectedYear] = useState(moment().year());

  const currentYear = moment().year();
  const yearOptions = [];
  for (let i = 0; i < 3; i++) {
    yearOptions.push(currentYear - i);
  }

  const monthOptions = [
    { value: 1, label: "Januari" },
    { value: 2, label: "Februari" },
    { value: 3, label: "Maret" },
    { value: 4, label: "April" },
    { value: 5, label: "Mei" },
    { value: 6, label: "Juni" },
    { value: 7, label: "Juli" },
    { value: 8, label: "Agustus" },
    { value: 9, label: "September" },
    { value: 10, label: "Oktober" },
    { value: 11, label: "November" },
    { value: 12, label: "Desember" },
  ];

  const getMonthName = (month) => {
    const months = {
      1: "Januari",
      2: "Februari",
      3: "Maret",
      4: "April",
      5: "Mei",
      6: "Juni",
      7: "Juli",
      8: "Agustus",
      9: "September",
      10: "Oktober",
      11: "November",
      12: "Desember",
    };
    return months[month] || "";
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = JSON.stringify({
        month: selectedMonth,
        year: selectedYear,
      });

      const response = await Axios.get(
        `${urlAPI}/v1/inventory/dashboardPerbandingan`,
        {
          params: { params },
          headers: { Authorization: "Bearer " + getToken() },
          timeout: 30000,
        },
      );

      setData(response.data?.result);
    } catch (error) {
      console.error("Error fetching comparison data:", error);
      alert("Gagal memuat data. Silakan coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  const formatNominal = (val) => {
    const number = Math.round(parseFloat(val) || 0);
    return `${number.toLocaleString("id-ID")}`;
  };

  const formatPersen = (val) => {
    return `${val.toFixed(2)}%`;
  };

  // StatCard component
  const StatCard = ({
    title,
    value,
    icon,
    color,
    persen,
    isAnggaran = false,
  }) => {
    const persenValue = persen || 0;
    const isWarning = isAnggaran && persenValue > 90;

    return (
      <Card
        className="border-0 shadow-lg h-100"
        style={{
          borderRadius: "20px",
          background: color,
          position: "relative",
          overflow: "hidden",
          transition: "all .3s ease",
          minHeight: isMobile ? "150px" : "190px",
        }}
      >
        {/* Background Circle Top Right */}
        <div
          style={{
            position: "absolute",
            width: "110px",
            height: "110px",
            borderRadius: "50%",
            background: "rgba(255,255,255,.08)",
            top: "-35px",
            right: "-35px",
          }}
        />

        {/* Background Circle Bottom Left */}
        <div
          style={{
            position: "absolute",
            width: "70px",
            height: "70px",
            borderRadius: "50%",
            background: "rgba(255,255,255,.06)",
            bottom: "-18px",
            left: "-18px",
          }}
        />

        {/* Content */}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            padding: "18px 20px",
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: "rgba(255,255,255,.18)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "18px",
              flexShrink: 0,
            }}
          >
            {icon}
          </div>

          {/* Title */}
          <div
            style={{
              fontSize: isMobile ? "10px" : "12px",
              fontWeight: 700,
              letterSpacing: "1px",
              textTransform: "uppercase",
              color: "rgba(255,255,255,.95)",
              marginBottom: "6px",
            }}
          >
            {title}
          </div>

          {/* Value */}
          <div
            style={{
              fontSize: isMobile ? "20px" : persen === undefined ? "40px" : "25px",
              fontWeight: "bold",
              color: "#fff",
              lineHeight: 1.15,
              wordBreak: "break-word",
            }}
          >
            {formatNominal(value)}
          </div>

          {/* Persentase */}
          {persen !== undefined && (
            <div
              style={{
                marginTop: "8px",
                fontSize: isMobile ? "12px" : "15px",
                fontWeight: 600,
                color: "rgba(255,255,255,.9)",
              }}
            >
              {formatPersen(persen)} dari Bahan Medis
            </div>
          )}

          {/* Warning */}
          {isWarning && (
            <div
              style={{
                marginTop: "auto",
                background: "#DC2626",
                color: "#fff",
                borderRadius: "10px",
                padding: "10px 12px",
                fontSize: isMobile ? "10px" : "12px",
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              ⚠️ Bahan Medis telah mencapai {formatPersen(persenValue)} dari
              Anggaran Bulan Ini!
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div
      style={{
        background: "#F0F2F5",
        minHeight: "100vh",
        padding: isMobile ? "12px" : "24px",
      }}
    >
      {/* Header dengan tombol back dan filter di kanan */}
      <div className="mb-4">
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "flex-start",
            gap: "20px",
          }}
        >
          {/* Bagian Kiri - Judul */}
          <div style={{ flex: 1 }}>
            <Button
              color="link"
              onClick={onBack}
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
                borderRadius: "12px",
                padding: isMobile ? "8px 16px" : "8px 20px",
                color: "white",
                fontWeight: "600",
                fontSize: isMobile ? "12px" : "14px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                transition: "transform 0.2s, box-shadow 0.2s",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                marginBottom: "16px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
              }}
            >
              <Icons.ArrowLeft size={isMobile ? 16 : 18} />
              Kembali ke Dashboard
            </Button>
            <h1
              style={{
                fontSize: isMobile ? "20px" : "24px",
                fontWeight: "bold",
                marginBottom: "8px",
                color: "#1a1a2e",
              }}
            >
              Perbandingan Omset & Bahan Medis
            </h1>
            <p
              className="mb-0"
              style={{
                fontSize: isMobile ? "11px" : "14px",
                color: "#000000",
                fontWeight: "600",
              }}
            >
              Perbandingan nilai omset, farmasi, dan anggaran dengan bahan medis
            </p>
          </div>

          {/* Bagian Kanan - Filter Bulan & Tahun + Periode */}
          <div
            style={{
              background: "white",
              padding: "15px 20px",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              minWidth: isMobile ? "100%" : "280px",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
                alignItems: "flex-end",
              }}
            >
              <div style={{ flex: 1, minWidth: "120px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    marginBottom: "4px",
                    display: "block",
                    color: "#555",
                  }}
                >
                  Bulan
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "13px",
                    backgroundColor: "white",
                    cursor: "pointer",
                  }}
                >
                  {monthOptions.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: "100px" }}>
                <label
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    marginBottom: "4px",
                    display: "block",
                    color: "#555",
                  }}
                >
                  Tahun
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "13px",
                    backgroundColor: "white",
                    cursor: "pointer",
                  }}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div
              style={{
                marginTop: "12px",
                paddingTop: "8px",
                borderTop: "1px solid #eee",
                textAlign: "center",
              }}
            >
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#667eea",
                }}
              >
                Periode: {getMonthName(selectedMonth)} {selectedYear}
              </span>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Memuat data...</p>
        </div>
      ) : data ? (
        <>
          {/* Card Bahan Medis - Baris pertama */}
          <Row className="mb-4 g-3">
            <Col xs={12}>
              <StatCard
                title="BAHAN MEDIS"
                value={data.summary?.bahan_medis || 0}
                icon={
                  <Icons.Activity size={isMobile ? 20 : 24} color="white" />
                }
                color="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              />
            </Col>
          </Row>

          {/* 3 Card: BAHAN MEDIS / ANGGARAN, BAHAN MEDIS / FARMASI, BAHAN MEDIS / OMSET - Baris kedua */}
          <Row className="mb-4 g-3">
            <Col xs={12} md={4}>
              <StatCard
                title="BAHAN MEDIS / ANGGARAN"
                value={data.summary?.total_anggaran || 0}
                icon={
                  <Icons.Calendar size={isMobile ? 20 : 24} color="white" />
                }
                color="linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)"
                persen={data.summary?.persen_bahan_medis_vs_anggaran || 0}
                isAnggaran={true}
              />
            </Col>
            <Col xs={12} md={4}>
              <StatCard
                title="BAHAN MEDIS / FARMASI"
                value={data.summary?.total_farmasi || 0}
                icon={<Icons.Package size={isMobile ? 20 : 24} color="white" />}
                color="linear-gradient(135deg, #134e5e 0%, #1e7b6e 100%)"
                persen={data.summary?.persen_bahan_medis_vs_farmasi || 0}
              />
            </Col>
            <Col xs={12} md={4}>
              <StatCard
                title="BAHAN MEDIS / OMSET"
                value={data.summary?.total_semua || 0}
                icon={
                  <Icons.DollarSign size={isMobile ? 20 : 24} color="white" />
                }
                color="linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)"
                persen={data.summary?.persen_bahan_medis_vs_total || 0}
              />
            </Col>
          </Row>

          {/* Tabel Per SBU */}
          <div>
            <h6
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                marginBottom: "12px",
                color: "#333",
              }}
            >
              Rincian per SBU
            </h6>

            <div
              style={{
                overflowX: "auto",
                borderRadius: "16px",
                background: "white",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: isMobile ? "12px" : "14px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                    }}
                  >
                    <th
                      style={{
                        padding: "14px 16px",
                        textAlign: "left",
                        fontWeight: "600",
                        borderRadius: "16px 0 0 0",
                      }}
                    >
                      SBU
                    </th>
                    <th
                      style={{
                        padding: "14px 16px",
                        textAlign: "right",
                        fontWeight: "600",
                      }}
                    >
                      Anggaran
                    </th>
                    <th
                      style={{
                        padding: "14px 16px",
                        textAlign: "right",
                        fontWeight: "600",
                      }}
                    >
                      Farmasi
                    </th>
                    <th
                      style={{
                        padding: "14px 16px",
                        textAlign: "right",
                        fontWeight: "600",
                        borderRadius: "0 16px 0 0",
                      }}
                    >
                      Omset
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_sbu
                    ?.filter(
                      (item) =>
                        item.sbu && item.sbu !== "" && item.sbu !== "NULL",
                    )
                    .map((item, idx) => {
                      const rowColor = idx % 2 === 0 ? "white" : "#F8F9FA";
                      const borderColors = {
                        RSPG: "#3B82F6",
                        GRAHU: "#8B5CF6",
                        DRIYO: "#06B6D4",
                        BOD: "#F59E0B",
                        "GUDANG BOD": "#10B981",
                      };
                      const borderColor = borderColors[item.sbu] || "#667eea";

                      return (
                        <tr
                          key={idx}
                          style={{
                            borderBottom:
                              idx !==
                              data.by_sbu.filter(
                                (i) =>
                                  i.sbu && i.sbu !== "" && i.sbu !== "NULL",
                              ).length -
                                1
                                ? "1px solid #e9ecef"
                                : "none",
                            backgroundColor: rowColor,
                            transition: "background-color 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#F1F5F9";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = rowColor;
                          }}
                        >
                          <td
                            style={{
                              padding: "12px 16px",
                              fontWeight: "600",
                              borderLeft: `4px solid ${borderColor}`,
                              color: "#1a1a2e",
                            }}
                          >
                            {item.sbu}
                          </td>
                          <td
                            style={{
                              padding: "12px 16px",
                              textAlign: "right",
                              fontWeight: "500",
                              color: "#7c3aed",
                            }}
                          >
                            {formatNominal(item.anggaran)}
                          </td>
                          <td
                            style={{
                              padding: "12px 16px",
                              textAlign: "right",
                              fontWeight: "500",
                              color: "#1e7b6e",
                            }}
                          >
                            {formatNominal(item.farmasi)}
                          </td>
                          <td
                            style={{
                              padding: "12px 16px",
                              textAlign: "right",
                              fontWeight: "500",
                              color: "#2a5298",
                            }}
                          >
                            {formatNominal(item.omset)}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-5 text-muted">
          <Icons.Inbox size={48} className="mb-3" />
          <p>Tidak ada data</p>
        </div>
      )}
    </div>
  );
};

export default PerbandinganPage;
